import { useEffect, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { FullPageSpinner } from '@/components/ui/Spinner';
import { useCan } from '@/hooks/useCan';
import { ForbiddenPage } from '@/pages/ForbiddenPage';
import { endSession } from '@/services/auth/session';
import { useAuthStore } from '@/stores/auth.store';
import type { AccessRule } from '@/utils/permissions';
import { paths } from './paths';
import { redirectTarget } from './redirect';

/** Signed-in area. Waits for session restore, then sends anonymous users to /login. */
export function ProtectedRoute() {
  const status = useAuthStore((s) => s.status);
  const location = useLocation();

  if (status === 'bootstrapping') return <FullPageSpinner />;
  if (status === 'anonymous')
    return <Navigate to={paths.login} replace state={{ from: location }} />;
  return <Outlet />;
}

/** Login and related pages. A signed-in user is sent where they were headed, or the dashboard. */
export function GuestRoute() {
  const status = useAuthStore((s) => s.status);
  const location = useLocation();

  if (status === 'bootstrapping') return <FullPageSpinner />;
  if (status === 'authenticated') return <Navigate to={redirectTarget(location.state)} replace />;
  return <Outlet />;
}

/**
 * Ends the session, then shows the login page. Logout is a route (outside ProtectedRoute) so the
 * protected area is left before the session is cleared — otherwise ProtectedRoute's own redirect
 * would race this one and remember the page the user logged out from.
 */
export function LogoutRoute() {
  const status = useAuthStore((s) => s.status);

  useEffect(() => {
    // Let a session restore finish first, or it could sign the user back in afterwards.
    if (status !== 'bootstrapping') void endSession();
  }, [status]);

  return status === 'anonymous' ? <Navigate to={paths.login} replace /> : <FullPageSpinner />;
}

/** Screen-level RBAC. The API enforces the same rule; this only avoids a dead-end screen. */
export function RequireAccess({ rule, children }: { rule: AccessRule; children: ReactNode }) {
  return useCan(rule) ? children : <ForbiddenPage />;
}
